import React from 'react';
import { useLocation } from 'react-router-dom';

import loadable from '@common/react/loadable/loadableSettings';
import Draggable, { DraggablePlace } from '@common/react/components/UI/Draggable';
import { useChatSettingsProviderContext } from '@common/react/components/Chat/ChatSettingsProvider';

const Chats = loadable(() =>
	import(/* webpackChunkName: "Chats" */ '@common/react/components/Chat/Chats'));

interface VideoChatModal {
	defaultPlace?: DraggablePlace;
	dragContainerPadding?: number;
	initFullMode?: boolean;
	onChangeFullMode?: (value: boolean) => void;
}

const ChatModal: React.FC<VideoChatModal> = (props) => {
	const location = useLocation();
	const {
		defaultPlace = 'leftBottom',
		dragContainerPadding = 30,
		initFullMode,
		onChangeFullMode,
	} = props;
	const [fullMode, _setFullMode] = React.useState(initFullMode);
	const setFullMode = (value) => {
		_setFullMode((prev) => {
			const newValue = typeof value === 'function' ? value(prev) : value;
			onChangeFullMode?.(newValue);
			return newValue;
		});
	};
	const { state: { modalMode }, actions: { setModalMode } } = useChatSettingsProviderContext();

	React.useEffect(() => {
		if (fullMode && modalMode) {
			if (typeof document === 'undefined') return;

			const elem = document.body;

			elem.style.overflow = 'hidden';

			return () => {
				elem.style.overflow = 'unset';
			};
		}
	}, [fullMode, modalMode]);

	React.useEffect(() => {
		if (modalMode && fullMode) {
			setModalMode(false);
		}
	}, [location.pathname]);

	if (!modalMode) {
		return null;
	}

	return (
		<Draggable
			className={`chat-modal ${fullMode ? 'chat-modal__full' : ''}`}
			isDraggable={!fullMode}
			isInOrigin={false}
			ignoreElements={['btn-video-chat-close', 'btn', 'chat-modal__close', 'chat-modal__full-mode',
				'chat-component', 'chat-component-list-content', 'chat-form-component', 'ant-modal-root',
				'ant-popover', 'ant-dropdown', 'ant-modal', 'ant-tooltip', 'ant-mentions-dropdown', 'ant-select-dropdown']}
			defaultPosition={defaultPlace}
			padding={dragContainerPadding}
		>
			<button className="chat-modal__full-mode" type="button" onClick={() => setFullMode((prev) => !prev)}>
				<i className={`fa fa-window-${fullMode ? 'minimize' : 'maximize'}`} />
			</button>
			<button className="chat-modal__close" type="button" onClick={() => setModalMode(false)}>
				<i className="fa fa-times" />
			</button>
			<div className="chat-modal__inner">
				<Chats />
			</div>
		</Draggable>
	);
};

export default ChatModal;
